import { useEffect, useState } from 'react';

import {
  graduationYearOptions,
  HIGH_SCHOOL_START_TERM,
  InfoModal,
  programStartYearOptions,
  withSavedYear,
} from '@backrs_ui/common-ui';

import { ReactComponent as BackrsFilledDropShadow } from '@backrs_ui/common-ui/src/assets/images/backrs-filled-drop-shadow.svg';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useGradeChangeConfirm } from '@backrs_ui/grow-ui/src/components/GradeChangeConfirm/useGradeChangeConfirm';
import {
  FIELD_KEYS_BY_LEVEL,
  UserProfile,
} from '@backrs_ui/grow-ui/src/components/ProfileCompletionModal/criticalProfileFields';
import {
  filterEditProfileProfileOptions,
  PROGRAM_START_TERM_OPTIONS,
} from '@backrs_ui/grow-ui/src/pages/Profile/utils/options.helpers';
import {
  ProfileFields,
  ProfileValueOptionResponse,
  useUpdateUserProfileFieldsMutation,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Box, Button, MenuItem, SxProps, TextField, Theme, Typography } from '@mui/material';

const STRINGS = appStrings.modules.profileCompletionModal;

type ProfileCompletionModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  profile: UserProfile | undefined;
  profileOptions: ProfileValueOptionResponse | undefined;
  /** The student has finished the program on file, so the ask is what's next. */
  graduated: boolean;
}>;

export const ProfileCompletionModal = ({
  open,
  onClose,
  profile,
  profileOptions,
  graduated,
}: ProfileCompletionModalProps) => {
  const [enrollmentId, setEnrollmentId] = useState('');
  const [graduationYear, setGraduationYear] = useState('');
  const [startTerm, setStartTerm] = useState('');
  const [startYear, setStartYear] = useState('');
  const [saveFailed, setSaveFailed] = useState(false);

  const [updateProfileFields, { isLoading: isSaving }] = useUpdateUserProfileFieldsMutation();
  const { confirmGradeChange, gradeChangeModal } = useGradeChangeConfirm();

  const savedEnrollmentId = profile?.academicEnrollmentTypes?.id;

  const enrollmentOptions =
    filterEditProfileProfileOptions(profileOptions)?.academicEnrollmentTypes ?? [];
  const selectedEnrollment = enrollmentOptions.find(
    (option) => option.id.toString() === enrollmentId,
  );

  useEffect(() => {
    setEnrollmentId(savedEnrollmentId?.toString() ?? '');
  }, [savedEnrollmentId]);

  // Year fields belong to the level of the selected enrollment type; "none" has no program.
  const level = selectedEnrollment?.level;
  const levelKeys = level && level !== 'none' ? FIELD_KEYS_BY_LEVEL[level] : null;

  const isHighSchool = level === 'high_school';

  const savedGraduationYear = (levelKeys && profile?.[levelKeys.graduationYear]?.toString()) || '';
  const savedStartTerm = (levelKeys && profile?.[levelKeys.startTerm]?.toString()) || '';
  const savedStartYear = (levelKeys && profile?.[levelKeys.startYear]?.toString()) || '';

  // Switching to a type at another level swaps in that level's saved values.
  useEffect(() => {
    setGraduationYear(savedGraduationYear);
    setStartTerm(savedStartTerm);
    setStartYear(savedStartYear);
  }, [savedGraduationYear, savedStartTerm, savedStartYear]);

  const patch: Partial<Record<ProfileFields, number | string>> = {};

  if (selectedEnrollment && selectedEnrollment.id !== savedEnrollmentId) {
    patch.academicEnrollmentTypes = selectedEnrollment.id;
  }
  if (levelKeys) {
    if (graduationYear && graduationYear !== savedGraduationYear) {
      patch[levelKeys.graduationYear] = Number(graduationYear);
    }
    if (isHighSchool) {
      if (startYear && savedStartTerm !== HIGH_SCHOOL_START_TERM) {
        patch[levelKeys.startTerm] = HIGH_SCHOOL_START_TERM;
      }
    } else if (startTerm && startTerm !== savedStartTerm) {
      patch[levelKeys.startTerm] = startTerm;
    }
    if (startYear && startYear !== savedStartYear) {
      patch[levelKeys.startYear] = startYear;
    }
  }

  const handleSave = async () => {
    if (Object.keys(patch).length === 0) {
      onClose();

      return;
    }

    // Leaves the form open when the student says the new year is wrong, so they
    // can correct the answer rather than lose it.
    if (!(await confirmGradeChange(patch))) return;

    setSaveFailed(false);
    try {
      await updateProfileFields(patch).unwrap();
      onClose();
    } catch {
      setSaveFailed(true);
    }
  };

  const yearFieldsDisabled = !levelKeys;

  const yearLevel = level && level !== 'none' ? level : null;

  // The prompt only ever asks about the program the student is in now, not one
  // already finished. A program has to finish after it starts, so each year
  // narrows the other's choices.
  const graduationYearChoices = withSavedYear(
    (yearLevel ? graduationYearOptions() : []).filter(
      (year) => !startYear || year > Number(startYear),
    ),
    graduationYear,
  ).map(String);
  const startYearChoices = withSavedYear(
    (yearLevel ? programStartYearOptions() : []).filter(
      (year) => !graduationYear || year < Number(graduationYear),
    ),
    startYear,
  ).map(String);

  return (
    <>
      {gradeChangeModal}
      <InfoModal
        open={open}
        onClose={onClose}
        width="xs"
        hideCircleImage
        headerVisual={<BackrsFilledDropShadow width="32px" height="32px" />}
        headerContent={
          <Box sx={headerContentSx}>
            <Typography variant="text-lg-semibold" sx={titleSx}>
              {graduated ? STRINGS.graduatedTitle : STRINGS.title}
            </Typography>
            <Typography variant="text-sm-regular" sx={subtitleSx}>
              {graduated ? STRINGS.graduatedSubtitle : STRINGS.subtitle}
            </Typography>
          </Box>
        }
        bodyContent={
          <Box sx={bodySx}>
            <Box sx={fieldsSx}>
              <TextField
                id="profile-completion-enrollment-select"
                label={STRINGS.enrollmentTypeLabel}
                value={enrollmentId}
                onChange={(event) => setEnrollmentId(event.target.value)}
                select
                fullWidth
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem id="profile-completion-enrollment-placeholder" value="" disabled>
                  {STRINGS.enrollmentTypePlaceholder}
                </MenuItem>
                {enrollmentOptions.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.id.toString()}
                    id={`profile-completion-enrollment-${option.id}`}
                  >
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                id="profile-completion-graduation-year-select"
                label={STRINGS.graduationYearLabel}
                value={graduationYear}
                onChange={(event) => setGraduationYear(event.target.value)}
                select
                fullWidth
                disabled={yearFieldsDisabled}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem id="profile-completion-graduation-year-placeholder" value="" disabled>
                  {STRINGS.graduationYearPlaceholder}
                </MenuItem>
                {graduationYearChoices.map((year) => (
                  <MenuItem
                    key={year}
                    value={year}
                    id={`profile-completion-graduation-year-${year}`}
                  >
                    {year}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={startFieldsRowSx}>
                {!isHighSchool && (
                  <TextField
                    id="profile-completion-start-term-select"
                    label={STRINGS.startTermLabel}
                    value={startTerm}
                    onChange={(event) => setStartTerm(event.target.value)}
                    select
                    fullWidth
                    disabled={yearFieldsDisabled}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem id="profile-completion-start-term-placeholder" value="" disabled>
                      {STRINGS.startTermPlaceholder}
                    </MenuItem>
                    {PROGRAM_START_TERM_OPTIONS.map((term) => (
                      <MenuItem
                        key={term.name}
                        value={term.name}
                        id={`profile-completion-start-term-${term.name.toLowerCase()}`}
                      >
                        {term.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  id="profile-completion-start-year-select"
                  label={STRINGS.startYearLabel}
                  value={startYear}
                  onChange={(event) => setStartYear(event.target.value)}
                  select
                  fullWidth
                  disabled={yearFieldsDisabled}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem id="profile-completion-start-year-placeholder" value="" disabled>
                    {STRINGS.startYearPlaceholder}
                  </MenuItem>
                  {startYearChoices.map((year) => (
                    <MenuItem key={year} value={year} id={`profile-completion-start-year-${year}`}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
            <Box sx={footerSx}>
              {saveFailed && (
                <Typography variant="text-sm-regular" sx={errorSx}>
                  {STRINGS.saveFailed}
                </Typography>
              )}
              <Button
                id="profile-completion-save"
                hierarchy="Primary"
                size="lg"
                fullWidth
                disabled={isSaving}
                onClick={handleSave}
              >
                {STRINGS.saveButton}
              </Button>
            </Box>
          </Box>
        }
      />
    </>
  );
};

const headerContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  px: backrs.spacing['spacing-xl'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  px: backrs.spacing['spacing-xl'],
  pt: backrs.spacing['spacing-2xl'],
  pb: backrs.spacing['spacing-md'],
});

const fieldsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-5xl'],
});

const startFieldsRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const footerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const errorSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
});
